import {CBARObject3DProperties} from "./CBARObject3D";
import {
    CBAREvent,
    CBAREventType,
    CBARHighlightState,
    CBARHistoryState,
    CBARMouseEvent,
    CBARSurfaceType,
    CBARToolMode,
    getSurfaceType
} from "../CBARTypes";

import {isDrawingTool} from "../CBARContext";

import * as THREE from "three";

import {Bounds, getCanvasMat, getIntensity} from "../internal/Utils";
import {Point} from "mirada";
import {CBARImaging, Labels} from "../internal/CBARImaging";
import {ErrorLog} from "../internal/GlobalLogger";
import {euclideanDist} from "../internal/Math";
import {CBARSurface, CBARSurfaceProperties, Scalar} from "./CBARSurface";

type DrawingPoint = {
    point:Point
    radius:number
    time:number
    drawn:boolean
}

const DEFAULT_DISTANCE_METERS = 4.0;
const DRAW_RADIUS_METERS = 0.04;
const DRAW_MAX_DISTANCE_METERS = 10;
export let DRAW_RADIUS = DRAW_RADIUS_METERS / DEFAULT_DISTANCE_METERS

const DRAWING_POINT_REFRESH = 300;
const DRAWING_POINT_TIMEOUT = DRAWING_POINT_REFRESH + 200;

const REFINE_EPSILON = 5;

export class CBARStaticSurface extends CBARSurface {

    public loadStatic(basePath:string|undefined, json:CBARSurfaceProperties, _verticalAxis:"z"|"y"|undefined, groundRotation:number, room?:string|null|undefined, subroom?:string|null|undefined) : Promise<CBARStaticSurface> {

        if (json.type ) {
            this.type = getSurfaceType(json.type)
        }

        const verticalAxis = _verticalAxis ? _verticalAxis : "z"
        //console.log("Creating surface", this.type, "axis", verticalAxis)

        if (json.color) {
            this.color = new THREE.Color(parseInt(json.color, 16))
        }

        if (json.normal) {
            this.plane.normal = verticalAxis === "y" ? new THREE.Vector3(json.normal[0], json.normal[1], json.normal[2]) : new THREE.Vector3(-json.normal[0], -json.normal[2], json.normal[1])
        }

        if (json.axisRotation) {
            this.axisRotation = verticalAxis === "y" ? json.axisRotation : -json.axisRotation;
        } else if (this.type == CBARSurfaceType.Floor || this.type == CBARSurfaceType.Ceiling) {
            this.axisRotation = this.type == CBARSurfaceType.Floor ? groundRotation : -1 * groundRotation;
        }

        if (json.backgroundMean) {
            this.backgroundMeanIntensity = getIntensity(json.backgroundMean)
        }

        if (json.backgroundStdDev) {
            this.backgroundStdDevIntensity = getIntensity(json.backgroundStdDev)
        }

        if (json.lightingMean) {
            this.lightingMeanIntensity = getIntensity(json.lightingMean)
        }

        if (json.lightingStdDev) {
            this.lightingStdDevIntensity = getIntensity(json.lightingStdDev)
        }

        // console.log(`Lighting ${this.hasCalculatedLighting ? "was pulled from json.\n" : "will be calculated.\n"}`,
        //     "backgroundMean", this.backgroundMeanIntensity, "lightingMean", this.lightingMeanIntensity);

        this.plane.constant = json.offset ? json.offset : Number.EPSILON;

        const promises:PromiseLike<any>[] = [super.load(basePath, json)];

        if (json.images) {
            promises.push(this.images.load(basePath, json.images, room, subroom));
        }

        return new Promise<CBARStaticSurface>((resolve, reject) => {
            Promise.all(promises).then(()=>{

                this._isSingleMask = this.images.containsKey("mask");

                if (this.isSingleMask && this.maskImage && this.maskImage.area) {
                    this.maskTexture?.loadImage(this.maskImage);
                    this.regenerateMeshes(true);
                }

                resolve(this)
            }).catch(error=>{
                reject(error)
            })
        })
    }

    private drawingMaybeChanged(toolMode:CBARToolMode) {

        if (!this.maskTexture) {
            console.log("No mask texture");
            return;
        }

        if (this.maskTexture?.isEditing) {
            if  (!this.isDrawingOn || !this.selected || !isDrawingTool(toolMode)) {
                this.stoppedEditing();
            }
        } else {
            if (this.selected && this.isDrawingOn && !this.maskTexture?.isEditing) {
                this.startedEditing();
            }
        }
    }

    private _drawingPoints:DrawingPoint[] = [];
    private _drawingPointsAreErase = false;

    private drawEraseAtPoint(point: THREE.Vector2) {
        this.drawingMaybeChanged(this.context.toolMode);

        const wasErase = this._drawingPointsAreErase;
        this._drawingPointsAreErase = this.context.toolMode === CBARToolMode.EraseSurface;
        if (wasErase !== this._drawingPointsAreErase) {
            this.clearPoints()
        }

        this._drawingPoints.push({point:point, radius:DRAW_RADIUS, time:performance.now(), drawn:false});
    }

    private _setClear = false;
    private clearPoints() {
        this._setClear = true;
    }

    private _lastDraw = 0;

    private startedEditing() {
        const mask = this.maskTexture;
        if (mask) {
            mask.isEditing = true;
        }

        this._commitTimer = window.setInterval(()=>{
            const maskTexture = this.maskTexture;
            const drawingCanvas = this.maskTexture?.canvas;
            const background = (this.context.gl.scene.background as THREE.Texture).image;
            //const background = this.context.scene?.lightingTexture?.canvas;

            if (!maskTexture || !drawingCanvas || !background) return;

            const min_age = (this._lastDraw ? this._lastDraw : performance.now()) - DRAWING_POINT_TIMEOUT;
            const pointsFiltered = this._drawingPoints.filter(p=>p.time > min_age || !p.drawn);

            if ((this._setClear || pointsFiltered.length) && !this._busy) {
                this._busy = true;
                this._lastDraw = performance.now();

                if (this._setClear) {
                    this._setClear = false;
                    this._drawingPoints = [];
                }

                //set to drawn
                pointsFiltered.forEach(p=>p.drawn = true);

                const points = [...pointsFiltered];
                //const start = performance.now();
                if (points.length > 0) {
                    this.commitDrawingChanges(this._drawingPointsAreErase, points, drawingCanvas, background).then(()=>{
                        this._busy = false;
                        maskTexture.showChanges(drawingCanvas);
                    }).catch(error=>{
                        //showDebugText(error.hasOwnProperty("message") ? error.message : error);
                        ErrorLog(error);
                    })
                }
            }

        }, DRAWING_POINT_REFRESH);
    }

    private stoppedEditing() {
        //showDebugText("stopped editing");

        if (this._commitTimer) clearInterval(this._commitTimer);

        const mask = this.maskTexture;
        if (mask) {
            mask.isEditing = false;
        }

        this.clearHistory();
        this.regenerateMeshes(false);
        this.context.refresh();
        this.needsUpdate();
    }

    private _commitTimer = 0;
    private _busy=false;

    private calculateBounds = (normalizedPoints:DrawingPoint[], padding:number)=>{
        const boundsNormalized = new Bounds(1,1,0,0);

        normalizedPoints.forEach((dp)=>{
            const p = dp.point;
            boundsNormalized.x1 = Math.min(boundsNormalized.x1, p.x);
            boundsNormalized.x2 = Math.max(boundsNormalized.x2, p.x);

            boundsNormalized.y1 = Math.min(boundsNormalized.y1, p.y);
            boundsNormalized.y2 = Math.max(boundsNormalized.y2, p.y);
        });

        boundsNormalized.x1 = Math.max(0, boundsNormalized.x1-padding);
        boundsNormalized.x2 = Math.min(boundsNormalized.x2+padding, 1);
        boundsNormalized.y1 = Math.max(0, boundsNormalized.y1-padding);
        boundsNormalized.y2 = Math.min(boundsNormalized.y2+padding, 1);

        return boundsNormalized;
    }

    private async commitDrawingChanges(isErase:boolean, normalizedPoints:DrawingPoint[], maskCanvas:HTMLCanvasElement, backgroundCanvas:HTMLCanvasElement) {

        const uncertainExpand = isErase ? 1.5 : 2.0;
        const padding = normalizedPoints[normalizedPoints.length - 1].radius * uncertainExpand * 1.1;
        const boundsNormalized = this.calculateBounds(normalizedPoints, padding);

        if (!boundsNormalized.area) return;

        const imageDiagonal = Math.hypot(maskCanvas.height, maskCanvas.width);

        //prepare images
        const mask = getCanvasMat(maskCanvas, boundsNormalized);
        if (!mask) return;
        cv.cvtColor(mask, mask, cv.COLOR_RGBA2GRAY);

        const background = getCanvasMat(backgroundCanvas, boundsNormalized);
        if (!background) return;
        cv.cvtColor(background, background, cv.COLOR_RGBA2RGB);

        //Smooth the bg, preserving edges:
        // const scratch = new cv.Mat(background.rows, background.cols, background.depth());
        // cv.bilateralFilter(background, scratch, 13, 80, 80, cv.BORDER_REFLECT);
        // scratch.copyTo(background);
        // scratch.delete();
        //imShow(background);

        //const offscreen = new OffscreenCanvas(background.cols, background.rows);

        if (background.cols != mask.cols || background.rows != mask.rows) {
            cv.resize(background, background, {width:mask.cols, height:mask.rows});
        }

        const xFactor = mask.cols / boundsNormalized.width;
        const yFactor = mask.rows / boundsNormalized.height;
        const factor = Math.min(xFactor, yFactor);

        const denormalizedPoints:DrawingPoint[] = normalizedPoints.map((dp)=>{
            const p = dp.point;
            return {
                point:{x:(p.x - boundsNormalized.x1) * xFactor, y:(p.y - boundsNormalized.y1) * yFactor},
                radius:dp.radius * factor,
                time:dp.time,
                drawn:dp.drawn
            }
        });

        //const boundsDiagonal = 2.0 * factor * Math.max(boundsNormalized.width - 2 * padding, boundsNormalized.height - 2 * padding);

        const first = denormalizedPoints[0];
        const uncertainPoints = denormalizedPoints.map((p)=>{
            return {...p, distance:euclideanDist(p.point, first.point)}
        })
            //.filter(p=>p.distance > Math.min(p.radius, boundsDiagonal - p.radius))
            .map(p=>{
                //const factor = 1 + p.distance / Math.pow(p.radius,  0.8);
                //const _uncertainExpand = 1 + (uncertainExpand -1) / factor;
                return {...p, radius:p.radius * uncertainExpand};
            });

        //build markers
        let markers = new cv.Mat();
        let maskLabel:Scalar
        let nonMaskLabel:Scalar

        // export enum Labels {
        //     barrier = -1,
        //     uncertain = 0,
        //     mask = 1,
        //     nonMask = 255,
        // }

        if (this.isSingleMask) {
            markers = cv.Mat.zeros(mask.rows, mask.cols, cv.CV_32S);
            maskLabel = CBARImaging.maskLabel;
            nonMaskLabel = CBARImaging.nonMaskLabel;

            markers.setTo(CBARImaging.nonMaskLabel);
            if (isErase) {
                markers.setTo(CBARImaging.maskLabel, mask);
                uncertainPoints.forEach(dp=>cv.circle(markers, dp.point, dp.radius, CBARImaging.uncertainLabel, cv.FILLED));
            } else {
                uncertainPoints.forEach(dp=>cv.circle(markers, dp.point, dp.radius, CBARImaging.uncertainLabel, cv.FILLED));
                markers.setTo(CBARImaging.maskLabel, mask);
            }

        } else {
            mask.convertTo(markers, cv.CV_32S, 1, 0);
            maskLabel = cv.Scalar.all(this.maskIndex)
            nonMaskLabel = CBARImaging.nonMaskLabel; //todo: invent first index not in all indices

            uncertainPoints.forEach(dp=>cv.circle(markers, dp.point, dp.radius, CBARImaging.uncertainLabel, cv.FILLED));
        }

        const radiusFactor = isErase ? 0.95 : 0.9;
        if (isErase) {
            denormalizedPoints.forEach(dp=>cv.circle(markers, dp.point, Math.min(dp.radius * radiusFactor, dp.radius - 2), nonMaskLabel, cv.FILLED));
        } else {
            denormalizedPoints.forEach(dp=>cv.circle(markers, dp.point, Math.min(dp.radius * radiusFactor, dp.radius - 2), maskLabel, cv.FILLED));
        }

        //imShow(markers);

        cv.watershed(background, markers);

        markers.convertTo(markers, cv.CV_8U);

        if (this.isSingleMask) {
            //convert to 255 mask, anything less than mask including barriers (-1)
            cv.threshold(markers, markers, Labels.mask - 1, 0, cv.THRESH_TOZERO);
        } else {
            cv.threshold(markers, markers, 0, 0, cv.THRESH_TOZERO); //get rid of barriers (-1)
        }

        //imShow(markers);

        //console.log("markers", this.maskIndex)
        //imShowOverlay(background, markers);

        //if (1 + 1 == 2) return


        // //snap to long edges
        // if (REFINE_EPSILON) {
        //     this.refineMask(mask, imageDiagonal);
        // }

        const pad = (REFINE_EPSILON ? REFINE_EPSILON * Math.sqrt(2) : 1);

        //imShow(mask);
        const maskContext = maskCanvas.getContext("2d", {willReadFrequently:true});
        if (maskContext) {
            const dx = maskCanvas.width * boundsNormalized.x1 + pad;
            const dy = maskCanvas.height * boundsNormalized.y1 + pad;

            cv.cvtColor(markers.roi({x:pad,y:pad,width:markers.cols-2*pad, height:markers.rows-2*pad}), markers, cv.COLOR_GRAY2RGBA);
            const array = new Uint8ClampedArray(markers.data, markers.cols, markers.rows);

            const imageData = maskContext.createImageData(markers.cols, markers.rows);
            imageData.data.set(array);

            maskContext.putImageData(imageData, dx, dy);
            maskContext.save();
        }

        background.delete();
        markers.delete();
        mask.delete();
    }

    protected undoHistory:CBARHistoryState[] = [];

    protected saveState() {
        const mask = this.maskTexture;
        if (mask?.canvas) {
            this.undoHistory.push(new CBARHistoryState(mask.canvas));
        }
    }

    protected restoreState(state:CBARHistoryState, completed?:(success:boolean)=>void) {
        const mask = this.maskTexture;
        if (mask?.canvas) {
            state.restoreInto(mask.canvas, ()=>{
                this.regenerateMeshes(false);
                mask.refresh();
                this.context.refresh();
                if (completed) completed(true);
            });
        } else if (completed) {
            completed(false);
        }
    }

    public get historyLength() {
        return this.undoHistory.length;
    }

    public undoLast(completed?:(success:boolean)=>void) {
        const state = this.undoHistory.pop();
        if (state) {
            this.restoreState(state, completed);
        } else {
            console.warn("Nothing to undo");
            if (completed) completed(false);
        }
    }

    public clearHistory() {
        this.undoHistory = [];
    }

    public revertChanges(completed?:(success:boolean)=>void) {
        if (this.undoHistory.length) {
            this.restoreState(this.undoHistory[0], (success:boolean)=>{
                this.clearHistory();
                if (completed) completed(success);
            });
        } else {
            //console.warn("Nothing to revert");
            if (completed) completed(false);
        }
    }

    public commitChanges(completed?:(success:boolean)=>void) {
        if (this.historyLength) {
            this.clearHistory();
            if (completed) completed(true);
        } else {
            console.warn("Nothing to commit");
            if (completed) completed(false);
        }
    }

    public get isDrawingOn() {
        return isDrawingTool(this.context.toolMode) && this.selected
    }

    public handleEvent(event: CBAREvent) {
        super.handleEvent(event);

        const me = event as CBARMouseEvent;

        //update drawing size if necessary:
        const intersection = isDrawingTool(this.context.toolMode) && me.point ? this.getPlaneIntersection(me.point) : undefined;

        if (intersection) {
            const distance = Math.min(DEFAULT_DISTANCE_METERS * 4, intersection.distanceTo(this.context.gl.camera.position));

            const selectedSurface = this.context.scene?.surfaceFor(CBARHighlightState.Selected);
            if (!selectedSurface || selectedSurface.id === this.id) {
                //const aspectRatio = this.context.gl.resolution.x / this.context.gl.resolution.y;
                DRAW_RADIUS = DRAW_RADIUS_METERS / Math.min(distance, DRAW_MAX_DISTANCE_METERS);
            }
        }

        switch (event.type) {

            case CBAREventType.TouchDown:
                if (!this.selected || this.context.toolMode === CBARToolMode.None
                    || (isDrawingTool(this.context.toolMode) && !this.context.scene?.surfaceFor(CBARHighlightState.Selected))) {
                    this.selected = true;
                }

                if (this.isDrawingOn) {
                    this.saveState();
                    this.drawEraseAtPoint(me.point);
                }
                break;

            case CBAREventType.TouchUp:
                if (this.isDrawingOn) {
                    this.drawEraseAtPoint(me.point);
                }
                this.clearPoints();

                break;

            case CBAREventType.DragMove:
                if (this.isDrawingOn) {
                    this.drawEraseAtPoint(me.point);
                }
                break;

            case CBAREventType.TouchMove:
                this.highlighted = true
                break;

            case CBAREventType.MouseOver:
                if (!this.highlighted) {
                    this.highlighted = true
                }
                break;
            case CBAREventType.MouseOut:
                if (me.surface) {
                    //console.log("Mouse out", me.surface.description)
                    me.surface.highlighted = false
                }
                break;
        }
    }

    removeFromScene() : void {
        super.removeFromScene();

        if (this._commitTimer) {
            window.clearInterval(this._commitTimer);
        }
    }
}

